import dayjs from 'dayjs';
import type { ColumnType } from 'antd/es/table';

/**
 * The identity columns of a passenger table, shared by the ShareJet booking
 * (SHAR-1064) and the Charter booking (CharterPassengers) so the two tables
 * stay the same: Date of birth, Gender, Issuing country, Expiry.
 */
type IdentityFields = {
  dateOfBirth?: string | null;
  gender?: string | null;
  nationality?: string | null;
  issuingCountry?: string | null;
  passportExpiry?: string | null;
  passportPending: boolean;
};

/**
 * SHAR-1063 — the apps store gender as typed ("male", "Male"); the table shows
 * it in capitals, as it shows the nationality, and the data is left alone.
 */
export function formatGender(g: string | null | undefined): string {
  return g?.trim() ? g.trim().toUpperCase() : '—';
}

const ddmmyyyy = (d: string | null | undefined) => (d ? dayjs(d).format('DD/MM/YYYY') : '—');

export function dobColumn<T extends IdentityFields>(): ColumnType<T> {
  return { title: 'Date of birth', key: 'dob', render: (_, p) => ddmmyyyy(p.dateOfBirth) };
}

export function genderColumn<T extends IdentityFields>(): ColumnType<T> {
  return { title: 'Gender', key: 'gender', render: (_, p) => formatGender(p.gender) };
}

/**
 * SHAR-1071 - countries are shown in capitals, like Gender ("MALE"), however
 * they were stored: rows saved before SHAR-1023 (which stores new values in
 * capitals) still hold "China" or "Angola".
 */
const upper = (v: string | null | undefined) => (v ? v.toLocaleUpperCase('en-GB') : '—');

export function nationalityColumn<T extends IdentityFields>(): ColumnType<T> {
  return { title: 'Nationality', key: 'nationality', render: (_, p) => upper(p.nationality) };
}

export function issuerColumn<T extends IdentityFields>(): ColumnType<T> {
  return {
    title: 'Issuing country',
    key: 'issuer',
    render: (_, p) => (p.passportPending ? '—' : upper(p.issuingCountry)),
  };
}

export function expiryColumn<T extends IdentityFields>(): ColumnType<T> {
  return { title: 'Expiry', key: 'exp', render: (_, p) => ddmmyyyy(p.passportExpiry) };
}

/**
 * SHAR-127 — one passenger's details as plain text, one line per value, for the
 * admin to paste into an email: the same values, formatted the same way, as
 * the passenger table shows them.
 */
export function passengerCopyText(p: IdentityFields & { fullName: string; passportNumber?: string | null }): string {
  const lines = [
    `Name: ${p.fullName}`,
    `Date of birth: ${ddmmyyyy(p.dateOfBirth)}`,
    `Gender: ${formatGender(p.gender)}`,
    `Nationality: ${upper(p.nationality)}`,
  ];
  if (p.passportPending) {
    lines.push('Passport: To be provided');
  } else {
    lines.push(
      `Passport number: ${p.passportNumber?.trim() || '—'}`,
      `Issuing country: ${upper(p.issuingCountry)}`,
      `Passport expiry: ${ddmmyyyy(p.passportExpiry)}`,
    );
  }
  return lines.join('\n');
}

/** SHAR-127 — put text on the clipboard; falls back to a hidden textarea where the API is absent. */
export async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(ta);
  if (!ok) throw new Error('copy failed');
}
